import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { money } from "@/lib/format";
import { entryTypeLabel } from "@/lib/inventory";
import { downloadCsv, toCsv } from "@/lib/csv";
import { firstRelation } from "@/lib/relations";
import { historicalProductKey, historicalProductName, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import {
  buildStockValuation,
  productCostValueMap,
  stockValuationCsv,
  STOCK_REPORT_VIEW_OPTIONS,
  type StockGrain,
} from "@/lib/inventory-valuation";
import { StockInventoryValueTable } from "@/pages/reports/StockInventoryValueTable";
import {
  averageOrderCycleTimeDays,
  median,
  supplierLeadTimeDays,
  lateDeliveryRate,
  fillRate,
  receiptVariance,
  returnRate,
  grossMargin,
  grossMarginRate,
  type Boundary,
  type PurchaseOrderDelivery,
} from "@/lib/metrics";

interface ProductRow { id: string; name: string; sku: string; cost_price: number | null; selling_price: number | null; reorder_point: number; status: string; }
interface VariantRow { id: string; product_id: string; sku: string | null; cost_price: number | null; selling_price: number | null; option_values: unknown; }
interface BalanceRow { product_id: string; variant_id: string | null; location_id: string; on_hand: number; allocated: number; available: number; products?: { name: string; sku: string } | null; locations?: { name: string } | null; }
interface PoRow { id: string; po_number: string; supplier_id: string | null; status: string; order_date: string | null; expected_date: string | null; total: number; suppliers?: { name: string } | null; }
interface SoRow { id: string; order_number: string; source: string; channel_id: string | null; status: string; order_date: string | null; total: number; created_at: string; customers?: { name: string } | null; locations?: { name: string } | null; }
interface SupplierRow { id: string; name: string; lead_time_days: number | null; }
interface LocationRow { id: string; name: string; }
interface ConnectorRow { id: string; name: string; }
interface LedgerRow extends CatalogueHistoryFields { id: string; product_id: string | null; qty_change: number; unit_cost: number | null; products?: { name: string } | null; }
interface MovementRow { entry_type: string; qty_change: number; }
interface PoItemRow { purchase_order_id: string; product_id: string; qty_ordered: number; qty_received: number; }
interface ReceiptRow { purchase_order_id: string; received_at: string | null; }
interface FulfilmentRow { sales_order_id: string; shipped_at: string | null; created_at: string; }
interface PickTaskRow { status: string; started_at: string | null; completed_at: string | null; }
interface ReturnRow { id: string; reason: string | null; status: string; }
interface ReturnItemRow { product_id: string; qty_returned: number; disposition: string | null; condition: string | null; }
interface SoItemRow extends CatalogueHistoryFields { id: string; sales_order_id: string; product_id: string | null; variant_id: string | null; qty_ordered: number; unit_price: number; total: number; product_name: string | null; sku: string | null; }
interface ListingRow { publication_status: string | null; last_error: string | null; last_synced_at: string | null; }
interface ExceptionRow { id: string; resolved_at: string | null; }

const inputCls = "px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

export default function Reports() {
  const { org } = useAuth();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [locationId, setLocationId] = useState("");
  const [stockGrain, setStockGrain] = useState<StockGrain>("product");

  const effFrom = dateFrom || new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const effTo = dateTo || new Date().toISOString().slice(0, 10);

  const stockQuery = useQuery({
    queryKey: ["reports-stock", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [products, variants, balances, locations] = await Promise.all([
        supabase.from("products").select("id, name, sku, cost_price, selling_price, reorder_point, status").eq("org_id", org!.id),
        supabase.from("variants").select("id, product_id, sku, cost_price, selling_price, option_values").eq("org_id", org!.id),
        supabase.from("inventory_balances").select("product_id, variant_id, location_id, on_hand, allocated, available, products(name, sku), locations(name)").eq("org_id", org!.id),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
      ]);
      const partialErrors = [
        products.error ? "products" : null,
        variants.error ? "variants" : null,
        balances.error ? "inventory balances" : null,
        locations.error ? "locations" : null,
      ].filter((x): x is string => !!x);
      return {
        products: (products.data ?? []) as ProductRow[],
        variants: (variants.data ?? []) as VariantRow[],
        balances: (balances.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          locations: firstRelation(row.locations),
        })) as BalanceRow[],
        locations: (locations.data ?? []) as LocationRow[],
        partialErrors,
      };
    },
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["reports", org?.id, dateFrom, dateTo, locationId],
    enabled: !!org?.id,
    queryFn: async () => {
      const defaultStart = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
      const periodStartDay = dateFrom || defaultStart.toISOString().slice(0, 10);
      const periodEndDay = dateTo || new Date().toISOString().slice(0, 10);
      const periodStartTs = dateFrom ? new Date(`${dateFrom}T00:00:00.000Z`).toISOString() : defaultStart.toISOString();
      const periodEndTs = dateTo ? new Date(`${dateTo}T23:59:59.999Z`).toISOString() : new Date().toISOString();

      let soQ = supabase
        .from("sales_orders")
        .select("id, order_number, source, channel_id, status, order_date, total, created_at, customers(name), locations(name)")
        .eq("org_id", org!.id)
        .gte("order_date", periodStartDay)
        .lte("order_date", periodEndDay);
      if (locationId) soQ = soQ.eq("location_id", locationId);

      let poQ = supabase
        .from("purchase_orders")
        .select("id, po_number, supplier_id, status, order_date, expected_date, total, suppliers(name)")
        .eq("org_id", org!.id)
        .gte("order_date", periodStartDay)
        .lte("order_date", periodEndDay);

      const [sos, pos, suppliers, connectors, ledger, movements, poItems, receipts, fulfilments, pickTasks, returns, returnItems, soItems, listings, exceptions] = await Promise.all([
        soQ,
        poQ,
        supabase.from("suppliers").select("id, name, lead_time_days").eq("org_id", org!.id).order("name"),
        supabase.from("connectors").select("id, name").eq("org_id", org!.id),
        supabase
          .from("inventory_ledger")
          .select("id, product_id, variant_id, catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, catalogue_snapshot_origin, entry_type, qty_change, unit_cost, products(name)")
          .eq("org_id", org!.id)
          .eq("entry_type", "sale_fulfilment")
          .gte("created_at", periodStartTs)
          .lte("created_at", periodEndTs),
        supabase
          .from("inventory_ledger")
          .select("entry_type, qty_change")
          .eq("org_id", org!.id)
          .gte("created_at", periodStartTs)
          .lte("created_at", periodEndTs),
        supabase.from("purchase_order_items").select("purchase_order_id, product_id, qty_ordered, qty_received").eq("org_id", org!.id).gte("created_at", periodStartTs).lte("created_at", periodEndTs),
        supabase.from("purchase_receipts").select("purchase_order_id, received_at").eq("org_id", org!.id).gte("received_at", periodStartTs).lte("received_at", periodEndTs),
        supabase.from("fulfilments").select("sales_order_id, shipped_at, created_at").eq("org_id", org!.id).gte("created_at", periodStartTs).lte("created_at", periodEndTs),
        supabase.from("pick_tasks").select("status, started_at, completed_at").eq("org_id", org!.id).gte("created_at", periodStartTs).lte("created_at", periodEndTs),
        supabase.from("sales_returns").select("id, reason, status").eq("org_id", org!.id).gte("created_at", periodStartTs).lte("created_at", periodEndTs),
        supabase.from("sales_return_items").select("product_id, qty_returned, disposition, condition").eq("org_id", org!.id).gte("created_at", periodStartTs).lte("created_at", periodEndTs),
        supabase.from("sales_order_items").select("id, sales_order_id, product_id, variant_id, catalogue_product_identity_id, catalogue_variant_identity_id, qty_ordered, unit_price, total, product_name, sku").eq("org_id", org!.id).gte("created_at", periodStartTs).lte("created_at", periodEndTs),
        supabase.from("channel_listings").select("publication_status, last_error, last_synced_at").eq("org_id", org!.id),
        supabase.from("integration_exceptions").select("id, resolved_at").eq("org_id", org!.id),
      ]);

      const partialErrors = [
        sos.error ? "sales orders" : null,
        pos.error ? "purchase orders" : null,
        suppliers.error ? "suppliers" : null,
        connectors.error ? "connectors" : null,
        ledger.error ? "fulfilment ledger" : null,
        movements.error ? "movement ledger" : null,
        poItems.error ? "purchase order items" : null,
        receipts.error ? "purchase receipts" : null,
        fulfilments.error ? "fulfilments" : null,
        pickTasks.error ? "pick tasks" : null,
        returns.error ? "returns" : null,
        returnItems.error ? "return items" : null,
        soItems.error ? "sales order items" : null,
        listings.error ? "channel listings" : null,
        exceptions.error ? "integration exceptions" : null,
      ].filter((x): x is string => !!x);

      return {
        sos: (sos.data ?? []).map((row) => ({
          ...row,
          customers: firstRelation(row.customers),
          locations: firstRelation(row.locations),
        })) as SoRow[],
        pos: (pos.data ?? []).map((row) => ({
          ...row,
          suppliers: firstRelation(row.suppliers),
        })) as PoRow[],
        suppliers: (suppliers.data ?? []) as SupplierRow[],
        connectors: (connectors.data ?? []) as ConnectorRow[],
        ledger: (ledger.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
        })) as LedgerRow[],
        movements: (movements.data ?? []) as MovementRow[],
        poItems: (poItems.data ?? []) as PoItemRow[],
        receipts: (receipts.data ?? []) as ReceiptRow[],
        fulfilments: (fulfilments.data ?? []) as FulfilmentRow[],
        pickTasks: (pickTasks.data ?? []) as PickTaskRow[],
        returns: (returns.data ?? []) as ReturnRow[],
        returnItems: (returnItems.data ?? []) as ReturnItemRow[],
        soItems: (soItems.data ?? []) as SoItemRow[],
        listings: (listings.data ?? []) as ListingRow[],
        exceptions: (exceptions.data ?? []) as ExceptionRow[],
        partialErrors,
      };
    },
  });

  const stockValuation = useMemo(() => {
    if (!stockQuery.data) return null;
    return buildStockValuation({
      products: stockQuery.data.products,
      variants: stockQuery.data.variants,
      balances: stockQuery.data.balances,
      locations: stockQuery.data.locations,
      locationId: locationId || null,
    });
  }, [stockQuery.data, locationId]);

  const metrics = useMemo(() => {
    if (!data || !stockQuery.data || !stockValuation) return null;
    const products = stockQuery.data.products;
    const balances = locationId
      ? stockQuery.data.balances.filter((b) => b.location_id === locationId)
      : stockQuery.data.balances;
    const costValueByProduct = productCostValueMap(stockValuation.lines);

    // Net sales explicitly excludes cancelled (and draft) orders.
    const netSales = data.sos
      .filter((s) => s.status !== "cancelled" && s.status !== "draft")
      .reduce((sum, s) => sum + Number(s.total ?? 0), 0);
    const fulfilledRevenue = data.sos.filter((s) => s.status === "fulfilled").reduce((sum, s) => sum + Number(s.total ?? 0), 0);

    // Gross margin — COGS from fulfilment ledger unit_cost; only report when complete.
    let cogs = 0;
    let cogsKnown = true;
    let fulfilmentLedgerCount = 0;
    for (const l of data.ledger) {
      fulfilmentLedgerCount += 1;
      const cost = l.unit_cost == null ? null : Number(l.unit_cost);
      if (cost == null) cogsKnown = false;
      else cogs += cost * Math.abs(Number(l.qty_change ?? 0));
    }
    const margin = cogsKnown && fulfilmentLedgerCount > 0 ? grossMargin(fulfilledRevenue, cogs) : null;
    const marginRate = cogsKnown && fulfilmentLedgerCount > 0 ? grossMarginRate(fulfilledRevenue, cogs) : null;
    const pendingPurchase = data.pos.filter((p) => p.status === "pending" || p.status === "partial").reduce((sum, p) => sum + Number(p.total ?? 0), 0);
    const toFulfil = data.sos.filter((s) => ["confirmed", "allocated", "partially_allocated", "ready_to_pick", "partially_fulfilled", "partial"].includes(s.status));

    // Channel / source dimension (excludes cancelled).
    const connectorName: Record<string, string> = {};
    for (const c of data.connectors) connectorName[c.id] = c.name;
    const bySource: Record<string, { count: number; total: number }> = {};
    for (const s of data.sos) {
      if (s.status === "cancelled" || s.status === "draft") continue;
      const key = s.source === "manual" ? "Internal / manual" : connectorName[s.channel_id ?? ""] ?? "Marketplace";
      const e = (bySource[key] = bySource[key] ?? { count: 0, total: 0 });
      e.count += 1;
      e.total += Number(s.total ?? 0);
    }

    // Sales by product (revenue + units) — from line items of non-cancelled/draft orders.
    const validSoIds = new Set(data.sos.filter((s) => s.status !== "cancelled" && s.status !== "draft").map((s) => s.id));
    const salesByProductMap: Record<string, { name: string; sku: string; units: number; revenue: number }> = {};
    const salesQtyByVariant: Record<string, number> = {};
    for (const it of data.soItems) {
      if (!validSoIds.has(it.sales_order_id)) continue;
      const key = historicalProductKey(it);
      const e = (salesByProductMap[key] = salesByProductMap[key] ?? { name: historicalProductName(it), sku: it.sku ?? "", units: 0, revenue: 0 });
      e.units += Number(it.qty_ordered ?? 0);
      e.revenue += Number(it.total ?? 0);
      if (it.variant_id) {
        salesQtyByVariant[it.variant_id] = (salesQtyByVariant[it.variant_id] ?? 0) + Number(it.qty_ordered ?? 0);
      }
    }
    const salesByProduct = Object.entries(salesByProductMap)
      .map(([productId, v]) => ({ productId, ...v }))
      .sort((a, b) => b.revenue - a.revenue);
    const totalProductRevenue = salesByProduct.reduce((s, p) => s + p.revenue, 0);

    // Supplier performance (from actual purchase data) — Part D.
    const today = new Date().toISOString().slice(0, 10);
    const itemsByPo: Record<string, { ordered: number; received: number }> = {};
    for (const it of data.poItems) {
      const e = (itemsByPo[it.purchase_order_id] = itemsByPo[it.purchase_order_id] ?? { ordered: 0, received: 0 });
      e.ordered += Number(it.qty_ordered ?? 0);
      e.received += Number(it.qty_received ?? 0);
    }
    const firstReceiptByPo: Record<string, string> = {};
    for (const r of data.receipts) {
      if (!r.received_at) continue;
      const cur = firstReceiptByPo[r.purchase_order_id];
      if (!cur || r.received_at < cur) firstReceiptByPo[r.purchase_order_id] = r.received_at;
    }
    const supplierPerf = data.suppliers
      .map((s) => {
        const supplierPos = data.pos.filter((p) => p.supplier_id === s.id);
        const volume = supplierPos.reduce((sum, p) => sum + Number(p.total ?? 0), 0);
        const outstanding = supplierPos.filter((p) => p.status === "pending" || p.status === "partial");
        const overdue = outstanding.filter((p) => p.expected_date && p.expected_date < today);

        const actualLeadTimes = supplierPos
          .map((p) => supplierLeadTimeDays(p.order_date, firstReceiptByPo[p.id] ?? null))
          .filter((d): d is number => d !== null);
        const deliveryEvidence: PurchaseOrderDelivery[] = supplierPos.map((p) => ({
          expectedDate: p.expected_date,
          receivedAt: firstReceiptByPo[p.id] ?? null,
          open: p.status === "pending" || p.status === "partial",
        }));
        let totalOrdered = 0;
        let totalReceived = 0;
        for (const p of supplierPos) {
          const line = itemsByPo[p.id];
          if (!line) continue;
          totalOrdered += line.ordered;
          totalReceived += line.received;
        }

        return {
          id: s.id,
          name: s.name,
          leadTime: s.lead_time_days,
          actualLeadTime: median(actualLeadTimes),
          poCount: supplierPos.length,
          volume,
          outstandingCount: outstanding.length,
          overdueCount: overdue.length,
          lateRate: lateDeliveryRate(deliveryEvidence, new Date()),
          fillRate: fillRate(totalReceived, totalOrdered),
          receiptVariance: receiptVariance(totalReceived, totalOrdered),
        };
      })
      .filter((x) => x.poCount > 0 || x.volume > 0)
      .sort((a, b) => b.volume - a.volume);

    // Order cycle time — Part C (created → shipped).
    const shippedAtBySo: Record<string, string> = {};
    for (const f of data.fulfilments) {
      const t = f.shipped_at ?? f.created_at;
      const cur = shippedAtBySo[f.sales_order_id];
      if (!cur || t < cur) shippedAtBySo[f.sales_order_id] = t;
    }
    const cycleOrders: Boundary[] = data.sos
      .filter((s) => s.status === "fulfilled" || s.status === "completed")
      .map((s) => ({ startedAt: s.created_at, completedAt: shippedAtBySo[s.id] ?? null }));
    const avgCycle = averageOrderCycleTimeDays(cycleOrders);
    const cycleDurations = cycleOrders
      .map((o) => (o.completedAt ? (new Date(o.completedAt).getTime() - new Date(o.startedAt).getTime()) / 86_400_000 : null))
      .filter((d): d is number => d !== null && d >= 0);

    // Warehouse throughput — Part E (real timestamps only).
    const completedPicks = data.pickTasks.filter((p) => p.status === "completed" || p.completed_at);
    const pickMinutes = completedPicks
      .map((p) => (p.started_at && p.completed_at ? (new Date(p.completed_at).getTime() - new Date(p.started_at).getTime()) / 60000 : null))
      .filter((d): d is number => d !== null && d >= 0);
    const ordersFulfilled = data.sos.filter((s) => s.status === "fulfilled" || s.status === "completed").length;

    // Return analytics — Part F.
    // Fulfilled units = units that left stock via sale fulfilment in the period
    // (authoritative ledger source), keeping return rate period-consistent.
    const fulfilledUnits = data.ledger.reduce((sum, l) => sum + Math.abs(Number(l.qty_change ?? 0)), 0);
    const returnedUnits = data.returnItems.reduce((sum, it) => sum + Number(it.qty_returned ?? 0), 0);
    const returnRateValue = returnRate(returnedUnits, fulfilledUnits);
    const reasonMap: Record<string, number> = {};
    for (const r of data.returns) {
      const key = r.reason && r.reason.trim() ? r.reason : "Unspecified";
      reasonMap[key] = (reasonMap[key] ?? 0) + 1;
    }
    const dispositionMap: Record<string, number> = {};
    for (const it of data.returnItems) {
      const key = it.disposition && it.disposition.trim() ? it.disposition : "Unspecified";
      dispositionMap[key] = (dispositionMap[key] ?? 0) + Number(it.qty_returned ?? 0);
    }

    // Velocity (units sold, last 30 days).
    const velocity: Record<string, number> = {};
    const velocityName: Record<string, string> = {};
    for (const l of data.ledger) {
      const units = Math.abs(Number(l.qty_change ?? 0));
      const key = historicalProductKey(l);
      velocity[key] = (velocity[key] ?? 0) + units;
      velocityName[key] = historicalProductName(l);
    }
    const topMovers = Object.entries(velocity)
      .map(([productId, units]) => ({ productId, name: velocityName[productId] ?? "—", units }))
      .sort((a, b) => b.units - a.units)
      .slice(0, 8);

    // Movement summary (last 30 days).
    const movementMap: Record<string, { count: number; net: number; units: number }> = {};
    for (const m of data.movements) {
      const t = m.entry_type ?? "unknown";
      const e = (movementMap[t] = movementMap[t] ?? { count: 0, net: 0, units: 0 });
      e.count += 1;
      e.net += Number(m.qty_change ?? 0);
      e.units += Math.abs(Number(m.qty_change ?? 0));
    }
    const movementSummary = Object.entries(movementMap)
      .map(([entry_type, e]) => ({ entry_type, ...e }))
      .sort((a, b) => b.units - a.units);

    // ABC Analysis — rank products by known inventory cost only (missing cost is not A$0).
    const onHandByProduct: Record<string, number> = {};
    const productNameMap: Record<string, string> = {};
    const productSkuMap: Record<string, string> = {};
    for (const p of products) {
      productNameMap[p.id] = p.name;
      productSkuMap[p.id] = p.sku;
    }
    for (const b of balances) {
      onHandByProduct[b.product_id] = (onHandByProduct[b.product_id] ?? 0) + Number(b.on_hand ?? 0);
    }
    const abcRaw = Object.entries(onHandByProduct)
      .map(([productId, onHand]) => ({
        productId,
        name: productNameMap[productId] ?? "—",
        sku: productSkuMap[productId] ?? "",
        onHand,
        value: costValueByProduct[productId]?.knownCostValue ?? 0,
      }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value);
    const totalAbcValue = abcRaw.reduce((s, x) => s + x.value, 0);
    let cumulative = 0;
    const abcAnalysis = abcRaw.map((item) => {
      const pct = totalAbcValue > 0 ? (item.value / totalAbcValue) * 100 : 0;
      cumulative += pct;
      const cls = cumulative <= 70 ? "A" : cumulative <= 90 ? "B" : "C";
      return { ...item, pct, cumulative, class: cls };
    });

    // Slow-moving / dead stock: on-hand > 0 with no sales in the period.
    const slowMoving = products
      .filter((p) => p.status !== "archived" && (onHandByProduct[p.id] ?? 0) > 0 && !((velocity[p.id] ?? 0) > 0))
      .map((p) => ({
        id: p.id,
        name: p.name,
        sku: p.sku,
        onHand: onHandByProduct[p.id] ?? 0,
        value: costValueByProduct[p.id]?.knownCostValue ?? 0,
        missingCost: (costValueByProduct[p.id]?.missingUnits ?? 0) > 0,
      }))
      .sort((a, b) => b.value - a.value);

    const medianCycle = median(cycleDurations);
    const avgPickMinutes = median(pickMinutes);

    // Low stock — current SKUs at/below reorder point (drill into replenishment).
    const availableByProduct: Record<string, number> = {};
    for (const b of balances) {
      availableByProduct[b.product_id] = (availableByProduct[b.product_id] ?? 0) + Number(b.available ?? 0);
    }
    const lowStock = products
      .filter((p) => p.status !== "archived" && (availableByProduct[p.id] ?? 0) <= Number(p.reorder_point ?? 0))
      .map((p) => ({ id: p.id, name: p.name, sku: p.sku, available: availableByProduct[p.id] ?? 0, reorderPoint: Number(p.reorder_point ?? 0) }))
      .sort((a, b) => a.available - b.available);

    // Channel sync health — marketplace listing + exception surface.
    const listingStatusMap: Record<string, number> = {};
    let listingsWithErrors = 0;
    let listingsStale = 0;
    const staleThresholdMs = Date.now() - 7 * 86_400_000;
    for (const l of data.listings) {
      const st = l.publication_status ?? "unknown";
      listingStatusMap[st] = (listingStatusMap[st] ?? 0) + 1;
      if (l.last_error) listingsWithErrors += 1;
      if (l.last_synced_at && new Date(l.last_synced_at).getTime() < staleThresholdMs) listingsStale += 1;
    }
    const openExceptions = data.exceptions.filter((e) => !e.resolved_at).length;

    return {
      netSales,
      fulfilledRevenue,
      pendingPurchase,
      toFulfil,
      bySource,
      supplierPerf,
      topMovers,
      movementSummary,
      abcAnalysis,
      avgCycle,
      medianCycle,
      cycleCount: cycleDurations.length,
      completedPicks: completedPicks.length,
      avgPickMinutes,
      ordersFulfilled,
      returnRateValue,
      returnedUnits,
      reasonMap,
      dispositionMap,
      lowStock,
      salesByProduct,
      salesQtyByVariant,
      totalProductRevenue,
      margin,
      marginRate,
      cogsKnown,
      slowMoving,
      listingStatusMap,
      listingsWithErrors,
      listingsStale,
      openExceptions,
      totalListings: data.listings.length,
    };
  }, [data, stockQuery.data, stockValuation, locationId]);

  const isReportsLoading = isLoading || stockQuery.isLoading;
  const isReportsError = isError || stockQuery.isError;

  if (isReportsLoading) return <ListSkeleton />;

  if (isReportsError || !data || !metrics || !stockQuery.data || !stockValuation) {
    return (
      <div>
        <PageHeader title="Reports" subtitle="Operational analytics and reporting" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load reports.</p>
          <button onClick={() => { void refetch(); void stockQuery.refetch(); }} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const statusCounts = (rows: { status: string }[]) => {
    const map: Record<string, number> = {};
    rows.forEach((r) => { map[r.status] = (map[r.status] ?? 0) + 1; });
    return map;
  };
  const soStatuses = statusCounts(data.sos);
  const poStatuses = statusCounts(data.pos);
  const stockSummary = stockValuation.summary;
  const partialErrors = [...(stockQuery.data.partialErrors ?? []), ...(data.partialErrors ?? [])];
  const reorderPointByProduct = Object.fromEntries(
    stockQuery.data.products.map((p) => [p.id, Number(p.reorder_point ?? 0)]),
  );

  const exportSales = () => {
    const headers = ["order_number", "customer", "location", "source", "status", "order_date", "total"];
    const rows = data.sos.map((s) => [s.order_number, s.customers?.name ?? "", s.locations?.name ?? "", s.source, s.status, s.order_date ?? "", s.total]);
    downloadCsv(`sales-report-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  };

  const exportSuppliers = () => {
    const headers = ["supplier", "lead_time_days", "actual_lead_time_days", "purchase_orders", "purchase_volume", "outstanding_pos", "overdue_pos", "late_delivery_rate", "fill_rate", "receipt_variance"];
    const rows = metrics.supplierPerf.map((s) => [
      s.name,
      s.leadTime ?? "",
      s.actualLeadTime != null ? s.actualLeadTime.toFixed(1) : "",
      s.poCount,
      s.volume,
      s.outstandingCount,
      s.overdueCount,
      s.lateRate != null ? (s.lateRate * 100).toFixed(1) : "",
      s.fillRate != null ? (s.fillRate * 100).toFixed(1) : "",
      s.receiptVariance != null ? (s.receiptVariance * 100).toFixed(1) : "",
    ]);
    downloadCsv(`supplier-performance-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  };

  const exportSalesByProduct = () => {
    const headers = ["product", "sku", "units", "revenue"];
    const rows = metrics.salesByProduct.map((p) => [p.name, p.sku, p.units, p.revenue]);
    downloadCsv(`sales-by-product-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  };

  const exportStockValue = () => {
    downloadCsv(`stock-inventory-value-${new Date().toISOString().slice(0, 10)}.csv`, stockValuationCsv(stockValuation.lines, stockGrain));
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Operational analytics and reporting" />

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Location</label>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputCls}>
              <option value="">All locations</option>
              {stockQuery.data.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          {(dateFrom || dateTo || locationId) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); setLocationId(""); }} className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 whitespace-nowrap">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {partialErrors.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <i className="ri-alert-line text-amber-600 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">Some data couldn't load</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {partialErrors.join(", ")} — these figures may be incomplete.
            </p>
          </div>
          <button onClick={() => { void refetch(); void stockQuery.refetch(); }} className="text-xs font-medium text-amber-800 hover:text-amber-900 whitespace-nowrap">
            Retry
          </button>
        </div>
      )}

      <p className="mb-4 text-xs text-slate-400">
        Sales, purchases, movements and supplier metrics use the reporting period: <span className="font-medium text-slate-600">{effFrom}</span> → <span className="font-medium text-slate-600">{effTo}</span>
        <span className="ml-1">(defaults to the last 90 days).</span>
        {" "}On-hand stock and inventory value are a current snapshot and do not change when the period is shortened or lengthened.
        {locationId ? " The location filter applies to both the snapshot and period sales." : ""}
      </p>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Link to="/sales-orders" className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Sales</span>
          <p className="text-2xl font-bold text-slate-900 mt-2">{money(metrics.netSales)}</p>
          <p className="text-[11px] text-slate-400 mt-1">Excludes cancelled and draft orders</p>
        </Link>
        <Link to="/sales-orders" className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Fulfilled revenue</span>
          <p className="text-2xl font-bold text-slate-900 mt-2">{money(metrics.fulfilledRevenue)}</p>
          <p className="text-[11px] text-slate-400 mt-1">
            {metrics.marginRate != null ? `Gross margin ${(metrics.marginRate * 100).toFixed(1)}%` : "Insufficient cost data"}
          </p>
        </Link>
        <Link to="/purchase-orders" className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Pending purchases</span>
          <p className="text-2xl font-bold text-slate-900 mt-2">{money(metrics.pendingPurchase)}</p>
        </Link>
        <Link to="/inventory" className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Inventory valuation</span>
          <p className="text-2xl font-bold text-slate-900 mt-2">{money(stockSummary.inventoryValueAtCost)}</p>
          <p className={`text-[11px] mt-1 ${stockSummary.costValuationComplete ? "text-slate-400" : "text-amber-700"}`}>
            {stockSummary.costValuationComplete
              ? "Complete · current on-hand at catalogue cost"
              : `Partial — ${stockSummary.unitsWithKnownCost} of ${stockSummary.totalUnitsOnHand} units have known cost`}
          </p>
        </Link>
        <Link to="/sales-orders" className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Orders to fulfil</span>
          <p className="text-2xl font-bold text-slate-900 mt-2">{metrics.toFulfil.length}</p>
        </Link>
      </div>

      {/* Current stock snapshot — independent of reporting period */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Stock & Inventory Value</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Current on-hand snapshot from catalogue cost and selling price. Not a period movement figure.
              {stockSummary.costValuationComplete ? "" : " Cost valuation is partial because some units have no stored cost."}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs text-slate-500 whitespace-nowrap">
              View
              <select
                value={stockGrain}
                onChange={(e) => setStockGrain(e.target.value as StockGrain)}
                className={`${inputCls} ml-2 py-1.5`}
              >
                {STOCK_REPORT_VIEW_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </label>
            <button onClick={exportStockValue} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap flex items-center gap-1">
              <i className="ri-download-2-line" /> Export
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-px bg-slate-100 border-b border-slate-100">
          <div className="bg-white p-4">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Total units on hand</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{stockSummary.totalUnitsOnHand}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Units with known cost</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{stockSummary.unitsWithKnownCost}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Units missing cost</p>
            <p className={`text-lg font-bold mt-1 ${stockSummary.unitsMissingCost > 0 ? "text-amber-700" : "text-slate-900"}`}>{stockSummary.unitsMissingCost}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Inventory value at cost</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{money(stockSummary.inventoryValueAtCost)}</p>
            <p className={`text-[11px] mt-0.5 ${stockSummary.costValuationComplete ? "text-slate-400" : "text-amber-700"}`}>
              {stockSummary.costValuationComplete ? "Complete" : "Partial"}
            </p>
          </div>
          <div className="bg-white p-4">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Retail / selling value</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{money(stockSummary.retailValue)}</p>
            {stockSummary.unitsMissingSelling > 0 && (
              <p className="text-[11px] text-slate-400 mt-0.5">{stockSummary.unitsWithKnownSelling} units with a selling price</p>
            )}
          </div>
          <div className="bg-white p-4">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Potential gross margin</p>
            <p className="text-lg font-bold text-slate-900 mt-1">
              {stockSummary.potentialGrossMargin == null ? "—" : money(stockSummary.potentialGrossMargin)}
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {stockSummary.unitsInGrossMargin === stockSummary.totalUnitsOnHand
                ? "Where cost and selling price are both known"
                : `On ${stockSummary.unitsInGrossMargin} of ${stockSummary.totalUnitsOnHand} units`}
            </p>
          </div>
        </div>
        <StockInventoryValueTable
          grain={stockGrain}
          lines={stockValuation.lines}
          reorderPointByProduct={reorderPointByProduct}
          salesQtyByVariant={metrics.salesQtyByVariant}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Sales by status */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Sales Order Status</h3>
            <button onClick={exportSales} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap flex items-center gap-1">
              <i className="ri-download-2-line" /> Export
            </button>
          </div>
          <div className="p-4 space-y-3">
            {Object.entries(soStatuses).map(([status, count]) => (
              <div key={status}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="capitalize text-slate-600">{status}</span>
                  <span className="font-semibold text-slate-800">{count}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(count / Math.max(data.sos.length, 1)) * 100}%` }} />
                </div>
              </div>
            ))}
            {data.sos.length === 0 && <div className="py-4 text-center text-sm text-slate-400">No sales orders in this period.</div>}
          </div>
        </div>

        {/* Sales by channel / source */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Net Sales by Channel</h3>
          </div>
          <div className="divide-y divide-slate-50">
            {Object.entries(metrics.bySource).map(([source, v]) => (
              <div key={source} className="px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-slate-600">{source}</span>
                <div className="text-right">
                  <span className="text-sm font-semibold text-slate-800">{money(v.total)}</span>
                  <span className="text-xs text-slate-400 ml-2">{v.count} orders</span>
                </div>
              </div>
            ))}
            {Object.keys(metrics.bySource).length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No sales data in this period.</div>
            )}
          </div>
        </div>
      </div>

      {/* Sales by product */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Sales by Product</h3>
            <p className="text-xs text-slate-400 mt-0.5">Net revenue and units from non-cancelled orders in the period</p>
          </div>
          <button onClick={exportSalesByProduct} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap flex items-center gap-1">
            <i className="ri-download-2-line" /> Export
          </button>
        </div>
        {metrics.salesByProduct.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No line items in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Units</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Revenue</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Share</th>
                </tr>
              </thead>
              <tbody>
                {metrics.salesByProduct.slice(0, 30).map((p) => (
                  <tr key={p.productId} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">{p.name}</p>
                      {p.sku && <p className="text-xs text-slate-400 font-mono">{p.sku}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{p.units}</td>
                    <td className="px-4 py-3 text-sm text-slate-800 font-medium text-right">{money(p.revenue)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{metrics.totalProductRevenue > 0 ? ((p.revenue / metrics.totalProductRevenue) * 100).toFixed(1) : "0.0"}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {metrics.salesByProduct.length > 30 && (
          <p className="px-4 py-2.5 bg-slate-50/60 border-t border-slate-100 text-[11px] text-slate-500">
            Showing top 30 of {metrics.salesByProduct.length} products.
          </p>
        )}
      </div>

      {/* Channel sync health */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Channel Sync Health</h3>
          <p className="text-xs text-slate-400 mt-0.5">Marketplace listings and integration exceptions</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4">
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Listings</span>
            <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.totalListings}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Sync errors</span>
            <p className={`text-2xl font-bold mt-1 ${metrics.listingsWithErrors > 0 ? "text-red-600" : "text-slate-900"}`}>{metrics.listingsWithErrors}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Stale (&gt;7d)</span>
            <p className={`text-2xl font-bold mt-1 ${metrics.listingsStale > 0 ? "text-amber-600" : "text-slate-900"}`}>{metrics.listingsStale}</p>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Open exceptions</span>
            <p className={`text-2xl font-bold mt-1 ${metrics.openExceptions > 0 ? "text-red-600" : "text-slate-900"}`}>{metrics.openExceptions}</p>
          </div>
        </div>
        {Object.keys(metrics.listingStatusMap).length > 0 && (
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            {Object.entries(metrics.listingStatusMap).map(([status, count]) => (
              <span key={status} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-xs text-slate-600">
                {status} <span className="font-semibold text-slate-800">{count}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Supplier performance */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Supplier Performance</h3>
          <button onClick={exportSuppliers} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap flex items-center gap-1">
            <i className="ri-download-2-line" /> Export
          </button>
        </div>
        {metrics.supplierPerf.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No purchase data yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Supplier</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Declared lead</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actual lead</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Late rate</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fill rate</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Variance</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Volume</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">POs</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {metrics.supplierPerf.map((s) => (
                  <tr key={s.id} className="border-b border-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">{s.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{s.leadTime != null ? `${s.leadTime}d` : "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{s.actualLeadTime != null ? `${s.actualLeadTime.toFixed(1)}d` : "—"}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={s.lateRate != null && s.lateRate > 0.25 ? "text-red-600 font-medium" : "text-slate-600"}>
                        {s.lateRate != null ? `${(s.lateRate * 100).toFixed(0)}%` : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={s.fillRate != null && s.fillRate < 0.9 ? "text-amber-600 font-medium" : "text-slate-600"}>
                        {s.fillRate != null ? `${(s.fillRate * 100).toFixed(0)}%` : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={s.receiptVariance != null && s.receiptVariance > 0 ? "text-red-600 font-medium" : "text-slate-600"}>
                        {s.receiptVariance != null ? `${(s.receiptVariance * 100).toFixed(1)}%` : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{money(s.volume)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{s.poCount}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={s.overdueCount > 0 ? "text-red-600 font-medium" : "text-slate-500"}>{s.overdueCount}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="px-4 py-2.5 bg-slate-50/60 border-t border-slate-100 text-[11px] text-slate-500">
          Figures cover the selected period. Lead time is the supplier's stated lead time.
        </p>
      </div>

      {/* Order cycle time + warehouse throughput */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Order Cycle Time</h3>
            <p className="text-xs text-slate-400 mt-0.5">Order created → shipped (days)</p>
          </div>
          <div className="p-4 grid grid-cols-3 gap-4">
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Average</span>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.avgCycle != null ? metrics.avgCycle.toFixed(1) : "—"}<span className="text-sm font-normal text-slate-400"> d</span></p>
            </div>
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Median</span>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.medianCycle != null ? metrics.medianCycle.toFixed(1) : "—"}<span className="text-sm font-normal text-slate-400"> d</span></p>
            </div>
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Orders</span>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.cycleCount}</p>
            </div>
          </div>
          {metrics.cycleCount === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-400">No fulfilled orders with shipment timestamps in this period.</div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Warehouse Throughput</h3>
            <p className="text-xs text-slate-400 mt-0.5">Live timing from pick and ship</p>
          </div>
          <div className="p-4 grid grid-cols-3 gap-4">
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Picks done</span>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.completedPicks}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Median pick</span>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.avgPickMinutes != null ? metrics.avgPickMinutes.toFixed(0) : "—"}<span className="text-sm font-normal text-slate-400"> m</span></p>
            </div>
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Fulfilled</span>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metrics.ordersFulfilled}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Return analytics */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Return Analytics</h3>
            <p className="text-xs text-slate-400 mt-0.5">Return rate = returned units ÷ fulfilled units</p>
          </div>
          <div className="text-right">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Return rate</span>
            <p className={`text-xl font-bold ${metrics.returnRateValue != null && metrics.returnRateValue > 0.05 ? "text-red-600" : "text-slate-900"}`}>
              {metrics.returnRateValue != null ? `${(metrics.returnRateValue * 100).toFixed(1)}%` : "—"}
            </p>
          </div>
        </div>
        {metrics.returnedUnits === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No returns in this period.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-4 border-b lg:border-b-0 lg:border-r border-slate-100">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Returns by reason</h4>
              {Object.entries(metrics.reasonMap).map(([reason, count]) => (
                <div key={reason} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-600">{reason}</span>
                  <span className="font-semibold text-slate-800">{count}</span>
                </div>
              ))}
            </div>
            <div className="p-4">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Units by disposition</h4>
              {Object.entries(metrics.dispositionMap).map(([disposition, units]) => (
                <div key={disposition} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-600 capitalize">{disposition}</span>
                  <span className="font-semibold text-slate-800">{units}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Inventory valuation by location + top movers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Inventory Valuation by Location</h3>
            <p className="text-xs text-slate-400 mt-0.5">Current snapshot at catalogue cost</p>
          </div>
          <div className="divide-y divide-slate-50">
            {stockValuation.byLocation.map((row) => (
              <div key={row.locationId} className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-sm text-slate-600">{row.locationName}</span>
                <div className="text-right">
                  <span className="text-sm font-semibold text-slate-800">{money(row.inventoryValueAtCost)}</span>
                  <p className={`text-[11px] ${row.costValuationComplete ? "text-slate-400" : "text-amber-700"}`}>
                    {row.costValuationComplete
                      ? `${row.onHand} units`
                      : `Partial · ${row.unitsWithKnownCost} of ${row.onHand} units have known cost`}
                  </p>
                </div>
              </div>
            ))}
            {stockValuation.byLocation.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No on-hand inventory</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Top Movers <span className="text-slate-400 font-normal text-xs">(in period)</span></h3>
          </div>
          <div className="divide-y divide-slate-50">
            {metrics.topMovers.map((m, i) => (
              <div key={m.productId} className="px-4 py-3 flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">{i + 1}</span>
                <span className="flex-1 text-sm text-slate-700">{m.name}</span>
                <span className="text-sm font-semibold text-slate-800">{m.units} sold</span>
              </div>
            ))}
            {metrics.topMovers.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No sales in this period</div>
            )}
          </div>
        </div>
      </div>

      {/* Purchase order status */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Purchase Order Status</h3>
        </div>
        <div className="p-4 space-y-3">
          {Object.entries(poStatuses).map(([status, count]) => (
            <div key={status}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="capitalize text-slate-600">{status}</span>
                <span className="font-semibold text-slate-800">{count}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(count / Math.max(data.pos.length, 1)) * 100}%` }} />
              </div>
            </div>
          ))}
          {data.pos.length === 0 && <div className="py-4 text-center text-sm text-slate-400">No purchase orders in this period.</div>}
        </div>
      </div>

      {/* Movement summary */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Stock Movement Summary <span className="text-slate-400 font-normal text-xs">(in period)</span></h3>
        </div>
        {metrics.movementSummary.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No stock movements in this period</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Movement type</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Entries</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Units moved</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Net change</th>
                </tr>
              </thead>
              <tbody>
                {metrics.movementSummary.map((m) => (
                  <tr key={m.entry_type} className="border-b border-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">{entryTypeLabel[m.entry_type] ?? m.entry_type}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{m.count}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{m.units}</td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${m.net > 0 ? "text-emerald-700" : m.net < 0 ? "text-red-600" : "text-slate-500"}`}>
                      {m.net > 0 ? "+" : ""}{m.net}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Low stock */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Low Stock</h3>
            <p className="text-xs text-slate-400 mt-0.5">SKUs at or below reorder point</p>
          </div>
          <Link to="/replenishment" className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap">Open replenishment</Link>
        </div>
        {metrics.lowStock.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No SKUs at or below reorder point.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Available</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reorder point</th>
                </tr>
              </thead>
              <tbody>
                {metrics.lowStock.map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">{s.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{s.sku}</p>
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${s.available === 0 ? "text-red-600" : "text-slate-800"}`}>{s.available}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{s.reorderPoint}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Slow-moving / dead stock */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Slow-moving / Dead Stock</h3>
          <p className="text-xs text-slate-400 mt-0.5">On-hand stock with no sales in this period</p>
        </div>
        {metrics.slowMoving.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No slow-moving stock in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">On hand</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Value at cost</th>
                </tr>
              </thead>
              <tbody>
                {metrics.slowMoving.slice(0, 30).map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">{s.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{s.sku}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{s.onHand}</td>
                    <td className={`px-4 py-3 text-sm text-right font-medium ${s.missingCost ? "text-amber-700" : "text-slate-800"}`}>
                      {s.missingCost && s.value === 0 ? "Incomplete valuation" : money(s.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {metrics.slowMoving.length > 30 && (
          <p className="px-4 py-2.5 bg-slate-50/60 border-t border-slate-100 text-[11px] text-slate-500">
            Showing top 30 of {metrics.slowMoving.length} slow-moving products.
          </p>
        )}
      </div>

      {/* Inventory value classification */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Inventory Value Classification</h3>
              <p className="text-xs text-slate-400 mt-0.5">A = top 70% by value · B = next 20% · C = bottom 10%</p>
            </div>
          </div>
        </div>
        {metrics.abcAnalysis.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No valued inventory to classify</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">On hand</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Value</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">% of total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Class</th>
                </tr>
              </thead>
              <tbody>
                {metrics.abcAnalysis.slice(0, 20).map((item) => (
                  <tr key={item.productId} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">{item.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{item.sku}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.onHand}</td>
                    <td className="px-4 py-3 text-sm text-slate-800 font-medium text-right">{money(item.value)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.pct.toFixed(1)}%</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        item.class === "A" ? "bg-emerald-100 text-emerald-700" :
                        item.class === "B" ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>{item.class}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {metrics.abcAnalysis.length > 0 && (
          <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-100 flex gap-6 text-xs text-slate-500">
            {(["A", "B", "C"] as const).map((cls) => {
              const count = metrics.abcAnalysis.filter((i) => i.class === cls).length;
              const val = metrics.abcAnalysis.filter((i) => i.class === cls).reduce((s, i) => s + i.value, 0);
              return (
                <span key={cls}>
                  <span className="font-semibold text-slate-700">{cls}:</span> {count} products · {money(val)}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}